import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import WhatsAppLink from "@/components/WhatsAppLink";
import { eur, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import BackNav from "@/components/BackNav";

const ORDINARIA = [
  "Rimozione di foglie e polvere",
  "Rimozione dello sporco leggero",
  "Piccolo riordino del luogo",
  "Semplice risciacquo, quando necessario e appropriato",
  "Controllo visivo generale",
];

const APPROFONDITA = [
  "Pulizia più accurata della lapide",
  "Pulizia di statue e piccoli elementi decorativi",
  "Pulizia dei lumi",
  "Prodotti specifici e appropriati per marmo e superfici",
  "Rimozione dello sporco più persistente",
  "Riordino finale",
];

export default function Pulizia() {
  const [config, setConfig] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (!config) return <Loading />;

  const cta = (pulizia) => (user ? `/checkout?type=singola&pulizia=${pulizia}` : "/accedi");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-testid="page-pulizia">
      <BackNav />

      <div className="max-w-2xl">
        <p className="eyebrow mb-2">Servizio</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy mb-4">Pulizia</h1>
        <p className="text-body leading-relaxed">
          Tre livelli di attenzione per il luogo dei tuoi cari: dalla cura ordinaria sempre compresa
          in ogni nostro intervento, alla pulizia approfondita, fino agli interventi specialistici valutati su misura.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card-soft p-8 border-2 !border-[#d1e2d5] flex flex-col" data-testid="cleaning-included-card">
          <p className="eyebrow mb-1">Sempre compresa</p>
          <h3 className="font-serif text-2xl text-navy mb-1">Cura ordinaria <span className="text-[#4a7c59]">inclusa</span></h3>
          <p className="text-sm text-body mb-5">Quando effettuiamo un intervento, ci prendiamo cura del luogo anche attraverso queste piccole attenzioni. Senza costi aggiuntivi.</p>
          <ul className="space-y-1.5 text-sm text-body">
            {ORDINARIA.map((x) => (
              <li key={x} className="flex items-start gap-2"><Check size={15} className="text-[#4a7c59] mt-0.5 shrink-0" />{x}</li>
            ))}
          </ul>
        </div>

        <div className="card-soft p-8 flex flex-col" data-testid="cleaning-deep-card">
          <p className="eyebrow mb-1">Servizio aggiuntivo</p>
          <h3 className="font-serif text-2xl text-navy mb-1">Pulizia approfondita <span className="text-[#4a7c59]">+{eur(config.cleaning_ordinary_price)}</span></h3>
          <p className="text-sm text-body mb-5">Un'attenzione in più, eseguita con prodotti professionali e specifici per il tipo di superficie.</p>
          <ul className="space-y-1.5 text-sm text-body mb-6">
            {APPROFONDITA.map((x) => (
              <li key={x} className="flex items-start gap-2"><Check size={15} className="text-[#4a7c59] mt-0.5 shrink-0" />{x}</li>
            ))}
          </ul>
          <Link to={cta("approfondita")} data-testid="pulizia-approfondita-cta" className="btn-sage mt-auto self-start !py-2.5 text-sm">
            Procedi <ArrowRight size={15} />
          </Link>
        </div>

        <div className="card-ivory p-8 flex flex-col justify-between" data-testid="cleaning-specialist-card">
          <div>
            <p className="eyebrow mb-1">Su valutazione</p>
            <h3 className="font-serif text-2xl text-navy mb-2">Pulizia specialistica <span className="text-[#4a7c59] text-lg">· Preventivo</span></h3>
            <p className="text-sm text-body leading-relaxed">
              Per interventi particolarmente impegnativi possiamo valutare la necessità di un trattamento
              specialistico e, quando necessario, affidarci a professionisti qualificati, con prodotti
              professionali e trattamenti specifici per il tipo di superficie.
            </p>
          </div>
          <WhatsAppLink
            message={"Ciao, ti contatto tramite Sempre Accanto perché vorrei richiedere un preventivo per una pulizia profonda/specialistica del luogo in cui effettuare il servizio. Vorrei avere maggiori informazioni e sapere il costo indicativo. Grazie."}
            label="Richiedi un preventivo"
            testid="pulizia-specialistica-cta"
            className="btn-secondary mt-6 self-start"
          />
        </div>
      </div>
    </div>
  );
}
